// Static content shared across pages
export const SERVICES = [
  {
    slug: "custom-software",
    name: "Custom Software",
    tag: "engineering",
    short: "Enterprise apps, SaaS platforms, and legacy modernization built to scale.",
    icon: "Code2",
    span: "md:col-span-2",
    features: ["Enterprise architecture", "Microservices", "API-first", "Legacy modernization"],
  },
  {
    slug: "web-mobile",
    name: "Web & Mobile",
    tag: "product",
    short: "Performant React, Next.js, and native apps users love.",
    icon: "Smartphone",
    span: "",
    features: ["React / Next.js", "iOS Swift", "Android Kotlin", "React Native"],
  },
  {
    slug: "cloud-devops",
    name: "Cloud & DevOps",
    tag: "infra",
    short: "Scalable cloud infrastructure with automated deployment pipelines.",
    icon: "Cloud",
    span: "",
    features: ["AWS / Azure / GCP", "Kubernetes", "CI/CD", "Terraform"],
  },
  {
    slug: "ai-ml",
    name: "AI & Machine Learning",
    tag: "intelligence",
    short: "Intelligent products powered by modern ML and large language models.",
    icon: "BrainCircuit",
    span: "md:col-span-2",
    features: ["LLM integrations", "Predictive analytics", "Computer vision", "RAG pipelines"],
  },
  {
    slug: "design",
    name: "UI/UX Design",
    tag: "craft",
    short: "User-centered design from research to high-fidelity production assets.",
    icon: "Sparkles",
    span: "",
    features: ["User research", "Design systems", "Prototyping", "Usability testing"],
  },
  {
    slug: "consulting",
    name: "Tech Consulting",
    tag: "strategy",
    short: "Strategic guidance that aligns technology with business outcomes.",
    icon: "Compass",
    span: "",
    features: ["Architecture audits", "CTO-as-a-Service", "Roadmaps", "Team augmentation"],
  },
];

export const PROCESS = [
  {
    step: "01",
    title: "Discover",
    duration: "1–2 weeks",
    body: "Stakeholder interviews, requirements, market and technical feasibility. We surface assumptions before they cost time.",
  },
  {
    step: "02",
    title: "Design",
    duration: "2–3 weeks",
    body: "Journeys, wireframes, hi-fi UI and clickable prototypes. Tested with real users wherever it matters.",
  },
  {
    step: "03",
    title: "Build",
    duration: "6–12 weeks",
    body: "Two-week agile sprints. Demos every cycle. Working software in your hands the whole way.",
  },
  {
    step: "04",
    title: "Test",
    duration: "2–3 weeks",
    body: "Functional, performance and security audits. Cross-browser, accessibility, and UAT with your team.",
  },
  {
    step: "05",
    title: "Launch",
    duration: "Ongoing",
    body: "Production deployment, monitoring, documentation and a long-term partnership with clear SLAs.",
  },
];

export const TECH_LOGOS = [
  "React", "Next.js", "Vue", "Svelte", "TypeScript", "Node.js", "Python",
  "FastAPI", "Django", "Go", "Rust", "PostgreSQL", "MongoDB", "Redis",
  "GraphQL", "Kafka", "AWS", "Azure", "GCP", "Kubernetes", "Docker",
  "Terraform", "TensorFlow", "PyTorch", "OpenAI", "LangChain", "Figma",
  "Stripe", "Vercel", "Cloudflare", "Tailwind", "Framer",
];

export const VALUES = [
  { title: "Innovation First", body: "We embrace emerging technologies and unconventional approaches to solve hard problems.", icon: "Rocket" },
  { title: "Client Success", body: "Your outcomes are how we measure ours. Real metrics, not vanity ones.", icon: "Target" },
  { title: "Quality Code", body: "Clean, tested, observable systems that survive long past the launch party.", icon: "Code2" },
  { title: "Continuous Learning", body: "We invest in our team's growth — research days, conferences, and craft rituals.", icon: "BookOpen" },
  { title: "Transparency", body: "Clear pricing, weekly demos, no surprises. You always know exactly where the work stands.", icon: "Eye" },
  { title: "Collaboration", body: "We work with you, not for you. Co-located rituals even when we're remote.", icon: "Users" },
];

export const TIMELINE = [
  { year: "2016", title: "Founded", body: "Three engineers, one office, a long-term bet on software craft." },
  { year: "2018", title: "First enterprise", body: "Grew to ten people. Shipped our first Fortune 500 platform." },
  { year: "2020", title: "50 projects", body: "Crossed fifty successful engagements. Reinvested in research." },
  { year: "2021", title: "AWS Partner", body: "Achieved Advanced Tier — formal recognition of cloud expertise." },
  { year: "2023", title: "AI practice", body: "Launched a dedicated AI/ML team. Shipped our first LLM-native product." },
  { year: "2024", title: "200+ shipped", body: "50 craftspeople, 200+ projects, clients in 15 countries." },
];

export const TEAM = [
  { name: "Aarav Mehta", role: "Co-Founder & CEO", bio: "15+ years building software companies. Former Stripe.", avatar: "https://images.unsplash.com/photo-1758534063829-a72058381e21?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMGRhcmslMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3OTUyMjAwNnww&ixlib=rb-4.1.0&q=85" },
  { name: "Lena Park", role: "Chief Technology Officer", bio: "Ex-Google engineer, Ph.D. in distributed systems.", avatar: "https://images.pexels.com/photos/36623225/pexels-photo-36623225.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { name: "David Kim", role: "Principal Engineer", bio: "Kubernetes contributor, cloud architecture lead.", avatar: "https://images.unsplash.com/photo-1765776830139-72b2184dae5a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMGRhcmslMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3OTUyMjAwNnww&ixlib=rb-4.1.0&q=85" },
  { name: "Priya Patel", role: "Design Director", bio: "Built design systems at Airbnb and Shopify.", avatar: "https://images.pexels.com/photos/36623225/pexels-photo-36623225.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { name: "Sarah Chen", role: "AI Practice Lead", bio: "Applied research at Meta AI. LangChain core contributor.", avatar: "https://images.pexels.com/photos/36623225/pexels-photo-36623225.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { name: "Michael Torres", role: "Performance Engineer", bio: "Web performance specialist. Author of Speed Audits.", avatar: "https://images.unsplash.com/photo-1758534063829-a72058381e21?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMGRhcmslMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3OTUyMjAwNnww&ixlib=rb-4.1.0&q=85" },
  { name: "Aisha Bello", role: "Mobile Engineering Lead", bio: "Ships React Native and Flutter apps to millions.", avatar: "https://images.pexels.com/photos/36623225/pexels-photo-36623225.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { name: "Tomás Herrera", role: "Engagement Director", bio: "Translates business goals into shipped software.", avatar: "https://images.unsplash.com/photo-1765776830139-72b2184dae5a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMGRhcmslMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3OTUyMjAwNnww&ixlib=rb-4.1.0&q=85" },
];

export const STATS = [
  { label: "Projects delivered", value: 200, suffix: "+" },
  { label: "Client satisfaction", value: 98, suffix: "%" },
  { label: "Specialists on team", value: 50, suffix: "+" },
  { label: "Years of practice", value: 8, suffix: "" },
];

export const NAV = [
  { label: "Services", href: "/services" },
  { label: "Projects", href: "/projects" },
  { label: "About", href: "/about" },
  { label: "Team", href: "/team" },
  { label: "Blog", href: "/blog" },
  { label: "Contact", href: "/contact" },
];
